import { memo, useCallback, useMemo, ReactNode } from 'react';
import { isEqual } from 'lodash';
import { EventsIcon } from '../assets/icons/EventsIcon';
import { PerksIcon } from '../assets/icons/PerksIcon';
import { GroupsIcon } from '../assets/icons/GroupsIcon';
import { MarketplaceIcon } from '../assets/icons/MarketplaceIcon';
import { ExternalLinkIcon } from '../assets/icons/ExternalLinkIcon';
import { UniversalButtonData } from '@cobu/types';
import { useUniversalButton } from '../hooks/universalButton/useUniversalButton';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { useTheme, Button, getTokens } from 'tamagui';
import { CobuStack, CobuText } from '@cobu/ui';
import { getFloatingBottomOffset } from '../constants/layout';
import { Strings } from '../constants/Strings';

const UniversalButtonContainer = () => {
  // const universalButtonData = useAppSelector(state => state.auth.universalButtonData);
  const universalButtonData = useSelector((state: RootState) => state.auth.universalButtonData);

  if (!universalButtonData) {
    return null;
  }

  return <UniversalButton universalButtonData={universalButtonData} />;
};

const FloatingContainer = ({ children }: { children: ReactNode }) => {
  const tokens = getTokens();
  const baseBottom = tokens.space.$8.val;
  const bottomOffset = getFloatingBottomOffset(baseBottom);

  return (
    <CobuStack
      style={{
        position: 'fixed',
        bottom: bottomOffset,
        left: '2.5%',
        width: '45%',
        zIndex: 1000,
      }}
    >
      {children}
    </CobuStack>
  );
};

const UniversalButton = memo(
  ({ universalButtonData }: { universalButtonData: UniversalButtonData | null }) => {
    const theme = useTheme();
    const { onPress, isDisabled, currentPageName } = useUniversalButton(universalButtonData);

    const getIcon = useCallback(() => {
      switch (universalButtonData?.linkType) {
        case 'Referral':
          return <PerksIcon isActive={false} color={theme?.white?.val || '#FFFFFF'} />;
        case 'Events':
          return <EventsIcon isActive={false} color={theme?.white?.val || '#FFFFFF'} />;
        case 'Groups':
          return <GroupsIcon isActive={false} color={theme?.white?.val || '#FFFFFF'} />;
        case 'Perks':
          return <PerksIcon isActive={false} color={theme?.white?.val || '#FFFFFF'} />;
        case 'Marketplace':
          return <MarketplaceIcon isActive={false} color={theme?.white?.val || '#FFFFFF'} />;
        case 'External':
          return <ExternalLinkIcon color={theme?.white?.val || '#FFFFFF'} />;
        case 'CreateGroupEvent':
          return <GroupsIcon isActive={false} color={theme?.white?.val || '#FFFFFF'} />;
        default:
          return null;
      }
    }, [universalButtonData?.linkType, currentPageName]);

    const buttonContent = useMemo(
      () => (
        <FloatingContainer>
          <Button
            onPress={onPress}
            disabled={isDisabled}
            opacity={isDisabled ? 0.6 : 1}
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            paddingVertical={6}
            paddingHorizontal={6}
            h="auto"
            borderRadius={24}
            backgroundColor="$buttonBackground"
            pressStyle={{
              backgroundColor: '$buttonBackground',
            }}
            hoverStyle={{
              backgroundColor: '$buttonBackground',
            }}
            focusStyle={{
              backgroundColor: '$buttonBackground',
            }}
          >
            {getIcon()}
            <CobuText col="$buttonText" ml="$2">
              {universalButtonData?.buttonTitle || Strings.get().ui.universalButton.fallbackTitle}
            </CobuText>
          </Button>
        </FloatingContainer>
      ),
      [onPress, isDisabled, getIcon, universalButtonData?.buttonTitle]
    );

    return buttonContent;
  },
  (prevProps, nextProps) => {
    return isEqual(prevProps.universalButtonData, nextProps.universalButtonData);
  }
);

export default UniversalButtonContainer;
